import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API_BASE_URL } from '../config/api';
import { Check, X, Package, Briefcase, MailPlus, UserPlus, CalendarPlus } from 'lucide-react';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const PLAN_KEYS = ['basic', 'growth', 'premium', 'ultra'];

const StatusBadge = ({ status }) => {
  const map = {
    pending: 'bg-amber-100 text-amber-800', approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-700', new: 'bg-blue-100 text-blue-800',
    active: 'bg-green-100 text-green-800', expired: 'bg-red-100 text-red-700'
  };
  return <Badge className={`${map[status] || 'bg-gray-100 text-gray-700'} text-[10px] capitalize`}>{status}</Badge>;
};

export default function AdminMarket() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [counts, setCounts] = useState({});
  const [enquiries, setEnquiries] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [sellerTotals, setSellerTotals] = useState({});
  const [digestLast, setDigestLast] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/'); return; }
    fetchAll();
  }, [user, authLoading]);

  const fetchAll = () => { fetchListings(); fetchEnquiries(); fetchSellers(); fetchDigestStatus(); };

  const fetchListings = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/market/admin/listings`, { headers: authHeaders() });
      setListings(data.listings || []);
      setCounts(data.counts || {});
    } catch (e) { console.error(e); }
  };

  const fetchEnquiries = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/market/admin/enquiries`, { headers: authHeaders() });
      setEnquiries(data.enquiries || []);
    } catch (e) { console.error(e); }
  };

  const fetchSellers = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/market/admin/sellers`, { headers: authHeaders() });
      setSellers(data.sellers || []);
      setPayments(data.payments || []);
      setSellerTotals(data.totals || {});
    } catch (e) { console.error(e); }
  };

  const fetchDigestStatus = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/market/admin/digest/status`, { headers: authHeaders() });
      setDigestLast(data.last);
    } catch (e) { console.error(e); }
  };

  const sendDigest = async () => {
    if (!window.confirm('Email the weekly Market Place digest (listings approved since the last digest) to ALL approved members now?')) return;
    setBusy('digest');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/market/admin/digest/send`, {}, { headers: authHeaders() });
      alert(data.message);
      fetchDigestStatus();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to send digest');
    } finally { setBusy(null); }
  };

  const moderate = async (id, action) => {
    let note = null;
    if (action === 'reject') {
      note = window.prompt('Optional note to the member (why it was rejected):') || null;
    }
    setBusy(id);
    try {
      await axios.post(`${API_BASE_URL}/api/market/admin/listings/${id}/moderate`, { action, note }, { headers: authHeaders() });
      fetchListings();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };

  const extendSeller = async (s) => {
    if (!window.confirm(`Extend ${s.name} (${s.plan_name}) by 1 year?`)) return;
    setBusy(s.id);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/market/admin/sellers/${s.id}/extend`, {}, { headers: authHeaders() });
      alert(`Extended until ${String(data.expires_at).slice(0, 10)}`);
      fetchSellers();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };

  const activateSeller = async () => {
    const email = window.prompt('Member email to activate as seller (offline payment):');
    if (!email) return;
    const plan = window.prompt(`Plan? Enter one of: ${PLAN_KEYS.join(', ')}`, 'basic');
    if (!plan || !PLAN_KEYS.includes(plan.toLowerCase().trim())) { alert('Invalid plan'); return; }
    setBusy('activate');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/market/admin/sellers/activate`,
        { email: email.trim(), plan: plan.toLowerCase().trim() }, { headers: authHeaders() });
      alert(`Seller activated until ${String(data.expires_at).slice(0, 10)}`);
      fetchSellers();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to activate');
    } finally { setBusy(null); }
  };

  const sendReport = async () => {
    setBusy('report');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/market/admin/sellers/report/send`, {}, { headers: authHeaders() });
      alert(data.message);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to send report');
    } finally { setBusy(null); }
  };

  const pending = listings.filter(l => l.status === 'pending');

  const ListingRow = ({ l, showActions }) => (
    <Card className="p-4 mb-3" data-testid={`admin-listing-${l.id}`}>
      <div className="flex items-start gap-4">
        {l.image_url ? (
          <img src={l.image_url} alt="" className="h-16 w-16 rounded object-cover border flex-shrink-0" />
        ) : (
          <div className="h-16 w-16 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
            {l.type === 'product' ? <Package className="h-6 w-6 text-gray-400" /> : <Briefcase className="h-6 w-6 text-gray-400" />}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold text-[#1E3A5F]">{l.title}</p>
            <StatusBadge status={l.status} />
            <Badge className="bg-gray-100 text-gray-600 text-[10px] capitalize">{l.type}</Badge>
            <span className="text-xs text-gray-500">{l.category}</span>
          </div>
          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{l.description}</p>
          <p className="text-xs text-gray-500 mt-1">
            {l.seller_org ? `${l.seller_org} · ` : ''}{l.seller_name} · {l.seller_email}
            {l.price_display ? ` · ${l.price_display}` : ''}{l.location ? ` · ${l.location}` : ''}
          </p>
        </div>
        {showActions && (
          <div className="flex gap-2 flex-shrink-0">
            <Button size="sm" onClick={() => moderate(l.id, 'approve')} disabled={busy === l.id}
              className="bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-listing-${l.id}`}>
              <Check className="h-4 w-4 mr-1" />Approve
            </Button>
            <Button size="sm" variant="outline" onClick={() => moderate(l.id, 'reject')} disabled={busy === l.id}
              className="border-red-300 text-red-600 hover:bg-red-50" data-testid={`reject-listing-${l.id}`}>
              <X className="h-4 w-4 mr-1" />Reject
            </Button>
          </div>
        )}
      </div>
    </Card>
  );

  return (
    <AdminLayout>
      <div className="mb-8 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="admin-market-title">Market Place</h1>
          <p className="text-gray-600">Moderate listings, manage Seller Accounts (setup + annual rental) and track enquiries.</p>
        </div>
        <div className="text-right">
          <Button onClick={sendDigest} disabled={busy === 'digest'}
            className="bg-[#1E3A5F] hover:bg-[#16304f] text-white" data-testid="send-digest-btn">
            <MailPlus className="h-4 w-4 mr-2" />
            {busy === 'digest' ? 'Sending...' : 'Send Weekly Digest Now'}
          </Button>
          <p className="text-xs text-gray-400 mt-1.5" data-testid="digest-status">
            {digestLast?.sent_at && !digestLast?.initialized
              ? `Last sent ${new Date(digestLast.sent_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} · ${digestLast.listings} listing(s) to ${digestLast.recipients} member(s)`
              : 'Auto-sends weekly when there are new listings'}
          </p>
        </div>
      </div>

      <Tabs defaultValue="pending">
        <TabsList className="mb-6">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending ({counts.pending || 0})</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all-listings">All Listings ({listings.length})</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="tab-admin-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
          <TabsTrigger value="sellers" data-testid="tab-sellers">Sellers ({sellers.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending">
          {pending.length ? pending.map(l => <ListingRow key={l.id} l={l} showActions />) : (
            <Card className="p-8 text-center text-gray-500" data-testid="no-pending-listings">No listings awaiting approval.</Card>
          )}
        </TabsContent>

        <TabsContent value="all">
          {listings.length ? listings.map(l => <ListingRow key={l.id} l={l} showActions={l.status === 'pending'} />) : (
            <Card className="p-8 text-center text-gray-500">No listings yet.</Card>
          )}
        </TabsContent>

        <TabsContent value="enquiries">
          {enquiries.length ? enquiries.map(e => (
            <Card key={e.id} className="p-4 mb-3" data-testid={`admin-enquiry-${e.id}`}>
              <div className="flex items-center gap-2">
                <p className="font-bold text-[#1E3A5F]">{e.listing_title}</p>
                <StatusBadge status={e.status} />
                {e.rating && (
                  <span className="text-amber-500 text-sm" data-testid={`enquiry-rating-${e.id}`}>
                    {'★'.repeat(e.rating)}{'☆'.repeat(5 - e.rating)}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-700 mt-1">{e.message}</p>
              {e.rating_comment && <p className="text-sm text-gray-500 italic mt-1">"{e.rating_comment}"</p>}
              <p className="text-xs text-gray-500 mt-1">
                Buyer: {e.buyer_name} ({e.buyer_email}{e.buyer_phone ? `, ${e.buyer_phone}` : ''}) → Seller: {e.seller_name} ({e.seller_email})
              </p>
            </Card>
          )) : (
            <Card className="p-8 text-center text-gray-500">No enquiries yet.</Card>
          )}
        </TabsContent>

        <TabsContent value="sellers">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
            {[['Seller Accounts', sellerTotals.sellers, ''], ['Seller Revenue', sellerTotals.revenue, '₹'], ['Launch Offer Left', sellerTotals.launch_offer_remaining, '']].map(([label, val, pre]) => (
              <Card key={label} className="p-4">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-lg font-bold text-[#1E3A5F]">{pre}{Number(val || 0).toLocaleString('en-IN')}</p>
              </Card>
            ))}
          </div>
          <div className="mb-4 flex gap-2 flex-wrap">
            <Button variant="outline" onClick={activateSeller} disabled={busy === 'activate'} data-testid="activate-seller-btn">
              <UserPlus className="h-4 w-4 mr-2" />Activate Seller Manually (offline payment)
            </Button>
            <Button variant="outline" onClick={sendReport} disabled={busy === 'report'} data-testid="send-renewal-report-btn">
              <MailPlus className="h-4 w-4 mr-2" />{busy === 'report' ? 'Sending...' : 'Email Renewal Report'}
            </Button>
          </div>
          {sellers.length ? sellers.map(s => (
            <Card key={s.id} className="p-4 mb-3" data-testid={`seller-row-${s.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-[#1E3A5F]">{s.org || s.name}</p>
                    <StatusBadge status={s.active ? 'active' : 'expired'} />
                    <Badge className="bg-[#1E3A5F]/10 text-[#1E3A5F] text-[10px]">{s.plan_name}</Badge>
                    {s.launch_offer && <Badge className="bg-green-100 text-green-800 text-[10px]">Launch offer</Badge>}
                    {s.manual && <Badge className="bg-gray-100 text-gray-600 text-[10px]">Manual</Badge>}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {s.email} · {s.listings_used} listing(s) · active since {String(s.activated_at).slice(0, 10)} · {s.active ? 'renews' : 'expired'} {String(s.expires_at).slice(0, 10)}
                  </p>
                </div>
                <Button size="sm" variant="outline" onClick={() => extendSeller(s)} disabled={busy === s.id}
                  data-testid={`extend-seller-${s.id}`}>
                  <CalendarPlus className="h-3.5 w-3.5 mr-1.5" />Extend 1 yr
                </Button>
              </div>
            </Card>
          )) : (
            <Card className="p-8 text-center text-gray-500">No Seller Accounts yet.</Card>
          )}
          {payments.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-semibold text-[#1E3A5F] mb-2">Seller Payments</p>
              {payments.map((p, i) => (
                <Card key={i} className="p-3 mb-2 flex items-center justify-between text-sm">
                  <span className="text-gray-700">{p.name} ({p.email}) · {p.plan} · {p.kind}</span>
                  <span className="font-semibold text-[#1E3A5F]">₹{Number(p.amount).toLocaleString('en-IN')} <span className="text-xs text-gray-400 font-normal">{String(p.paid_at || '').slice(0, 10)}</span></span>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
}
